import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LucideIcon } from "lucide-react";
import { cn } from "@/components/utils/utils";
import { ReactNode } from "react";

interface EditableInputProps {
  label: string;
  value: string;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  icon?: LucideIcon;
  rightIcon?: ReactNode;
  isEditing: boolean;
  onChange: (value: string) => void;
  className?: string;
  maxLength?: number;
  wrapperClassName?: string;
  children?: ReactNode;
  onFocus?: () => void;
  onBlur?: () => void;
}

const EditableInput = ({
  label,
  value,
  placeholder,
  type = "text",
  disabled = false,
  icon: Icon,
  rightIcon,
  isEditing,
  onChange,
  className,
  maxLength,
  wrapperClassName,
  children,
  onFocus,
  onBlur
}: EditableInputProps) => {
  if (isEditing) {
    return (
      <div className={cn("space-y-2", wrapperClassName)}>
        <Label className="text-sm font-medium text-gray-700">
          {label}
        </Label>
        <div className="flex gap-2">
          <div className={cn("flex-1 relative", className)}>
            {Icon && (
              <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            )}
            <Input
              type={type}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                "h-12",
                Icon ? "pl-10" : "",
                rightIcon ? "pr-10" : "",
                disabled ? "bg-gray-50" : ""
              )}
              maxLength={maxLength}
              onFocus={onFocus}
              onBlur={onBlur}
            />
            {rightIcon && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                {rightIcon}
              </div>
            )}
          </div>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-1", wrapperClassName)}>
      <Label className="text-sm font-medium text-gray-700">
        {label}
      </Label>
      <div className={cn("min-h-[34px] flex items-center", className)}>
        <div className="flex-1 text-gray-900">
          {value || <span className="text-gray-400">{placeholder}</span>}
        </div>
      </div>
      <div className="h-px bg-gray-300 w-full"></div>
    </div>
  );
};

export default EditableInput;
